import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Spin, Upload } from "antd";
import type { UploadProps } from "antd";
import ImgCrop from "antd-img-crop";
import { LoadingOutlined } from "@ant-design/icons";
import CommonIcons from "@base/assets/icons";
import CommonImages from "@base/assets/images";
import { prepareAttachmentsForUpload } from "@base/helpers/attachmentUpload";
import accountService from "@base/modules/user/account/accountService";
import useGetUserInfo from "@base/modules/user/account/hooks/useGetUserInfo";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";
import { RootState } from "@base/store/store";

type UploadRequest = Exclude<UploadProps["customRequest"], undefined>;

/** `auth/user/upload-avatar` answers a bare 500 above 10 MB, so the limit is checked here first. */
const AVATAR_MAX_BYTES = 10 * 1024 * 1024;
const AVATAR_TYPES = ["image/png", "image/jpeg"];

const avatarError = (file: File) => {
  if (!AVATAR_TYPES.includes(file.type) && !/\.(png|jpe?g)$/i.test(file.name)) {
    return "user.accountSetting.avatarInvalidType";
  }

  return file.size > AVATAR_MAX_BYTES ? "user.accountSetting.avatarTooLarge" : "";
};

/** The account's avatar with its camera button: pick, crop, upload. */
export default function AvatarUploader() {
  //! state
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userInfo = useSelector((state: RootState) => state.appCommonSlice.userInfo);
  const { handleGetUserInfo } = useGetUserInfo();
  const [isUploading, setIsUploading] = useState(false);
  const [isBroken, setIsBroken] = useState(false);
  // ImgCrop still hands a file it refused to crop on to the upload; this stops it there.
  const isRejected = useRef(false);
  const link = userInfo?.user_profiles?.avatar?.[0]?.link;

  //! function
  // Checked before the crop dialog opens, so a wrong file is turned away before any cropping.
  const beforeCrop = (file: File) => {
    const error = avatarError(file);

    isRejected.current = !!error;

    if (error) {
      dispatch(setAlertMessage({ type: "error", message: t("Notification"), description: t(error) }));
    }

    return !error;
  };

  // Sent here rather than by antd's own upload, and awaited: reloading straight after starting it
  // cancelled the request before it left, so no avatar was ever saved. The new avatar is fetched
  // instead of reloading the page.
  const upload: UploadRequest = async ({ file, onSuccess, onError }) => {
    setIsUploading(true);

    try {
      // Shrunk like every other photo we send, which keeps it far below the API's 10 MB.
      const [prepared] = await prepareAttachmentsForUpload([file as File]);

      await accountService.uploadAvatar(prepared ?? file);
      await handleGetUserInfo();
      setIsBroken(false);
      onSuccess?.(null);
      dispatch(setAlertMessage({ type: "success", message: t("Notification"), description: t("user.accountSetting.avatarUpdated") }));
    } catch (error) {
      onError?.(error as Error);
      dispatch(setAlertMessage({ type: "error", message: t("Notification"), description: t("user.accountSetting.avatarUploadFailed") }));
    } finally {
      setIsUploading(false);
    }
  };

  //! render
  return (
    // Fixed size: a long name beside it used to squeeze the avatar into an oval.
    <div className="relative shrink-0">
      <img
        className="rounded-[50%] object-cover w-16 h-16"
        width={64}
        height={64}
        src={!isBroken && link ? link : CommonImages.DefaultAvatar}
        // A link the CDN no longer serves shows the default rather than a broken image.
        onError={() => setIsBroken(true)}
        alt=""
      />

      {isUploading ? (
        <div className="absolute inset-0 rounded-[50%] bg-black/40 flex items-center justify-center">
          <Spin indicator={<LoadingOutlined className="text-common-white text-xl" spin />} />
        </div>
      ) : null}

      <ImgCrop
        rotationSlider
        cropShape="round"
        quality={1}
        modalTitle={t("user.accountSetting.updateAvatar")}
        modalOk={t("Submit")}
        modalCancel={t("Cancel")}
        beforeCrop={beforeCrop}
      >
        <Upload
          className="absolute -right-2 -bottom-2"
          accept="image/png,image/jpeg"
          showUploadList={false}
          disabled={isUploading}
          beforeUpload={() => (isRejected.current ? Upload.LIST_IGNORE : true)}
          customRequest={upload}
        >
          <div
            className={`w-fit bg-common-primary100 rounded-[50%] text-common-primary p-[5px] outline outline-white outline-2 ${
              isUploading ? "cursor-not-allowed opacity-60" : "cursor-pointer"
            }`}
            title={t("user.accountSetting.updateAvatar")}
          >
            <CommonIcons.IconCamera />
          </div>
        </Upload>
      </ImgCrop>
    </div>
  );
}
